import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import { getDiagnosticReport } from '../services/api';
import logo from '../assets/logo.png';

const Report = () => {
  const { diagnosticId } = useParams();
  const navigate = useNavigate();

  const { data: diagnostic, isLoading } = useQuery({
    queryKey: ['diagnostic', diagnosticId],
    queryFn: () => getDiagnosticReport(diagnosticId),
  });

  if (isLoading)
    return (
      <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="text-center relative">
          <div className="w-16 h-16 border-4 border-[#9fc6ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Generating your report...</p>
        </div>
      </div>
    );

  const radarData = Object.entries(diagnostic?.category_scores || {}).map(
    ([name, score]) => ({
      category: name,
      score,
      fullMark: 100,
    })
  );

  const getRisk = (score) => {
    if (score >= 70)
      return {
        level: 'Low',
        color: 'text-green-400',
        bg: 'bg-green-900/30 border-green-700/50',
      };
    if (score >= 50)
      return {
        level: 'Moderate',
        color: 'text-yellow-400',
        bg: 'bg-yellow-900/30 border-yellow-700/50',
      };
    return {
      level: 'High',
      color: 'text-red-400',
      bg: 'bg-red-900/30 border-red-700/50',
    };
  };

  const isNewWeaknessFormat =
    diagnostic?.top_weaknesses?.[0] &&
    typeof diagnostic.top_weaknesses[0] === 'object';

  return (
    <div className="min-h-screen bg-[#05070d] text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative glow */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#05070d]/95 backdrop-blur-md border-b border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5">
          <div className="flex justify-between items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-[#9fc6ff] hover:text-white text-sm font-medium transition-colors"
            >
              ← Back to Dashboard
            </button>

            <Link to="/" className="flex items-center gap-3">
              <img
                src={logo}
                alt="RAL Real Advisor"
                className="w-10 h-10 object-contain"
              />
              <div className="flex items-center">
                <span className="text-xl font-serif font-bold text-white">
                  RAL
                </span>
                <span className="text-xl font-serif font-light text-[#9fc6ff] ml-1">
                  Real Advisor
                </span>
              </div>
            </Link>

            {diagnostic?.pdf_report ? (
              <a
                href={diagnostic?.pdf_report}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-2 bg-[#9fc6ff] text-[#05070d] text-sm font-medium hover:opacity-90 transition-all"
              >
                Download PDF
              </a>
            ) : (
              <span className="px-6 py-2 bg-white/10 text-neutral-400 text-sm border border-white/10">
                PDF Generating...
              </span>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12 relative">
        {/* Executive Summary */}
        <section className="bg-white/5 backdrop-blur border border-white/10 p-8 mb-8">
          <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-4 block">
            Executive Summary
          </span>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <p className="text-neutral-400 mb-2">Overall Maturity Score</p>
              <p className="text-7xl font-serif font-bold text-white mb-2">
                {diagnostic?.overall_score}
                <span className="text-2xl text-neutral-500">/100</span>
              </p>
              <span className="inline-block mt-4 px-6 py-2 bg-[#9fc6ff] text-[#05070d] font-medium">
                {diagnostic?.maturity_level}
              </span>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#404040" />
                  <PolarAngleAxis
                    dataKey="category"
                    tick={{ fontSize: 11, fill: '#a3a3a3' }}
                  />
                  <PolarRadiusAxis
                    angle={30}
                    domain={[0, 100]}
                    tick={{ fill: '#737373' }}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#9fc6ff"
                    fill="#9fc6ff"
                    fillOpacity={0.35}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Category Scores */}
        <section className="bg-white/5 backdrop-blur border border-white/10 p-8 mb-8">
          <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-6 block">
            Category Scores
          </span>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(diagnostic?.category_scores || {}).map(([cat, score]) => {
              const risk = getRisk(score);
              return (
                <div
                  key={cat}
                  className="bg-[#0b1220] border border-white/10 p-5"
                >
                  <h3 className="font-medium text-neutral-300 text-sm mb-3 tracking-wide uppercase">
                    {cat}
                  </h3>
                  <p className="text-3xl font-serif font-bold text-white mb-2">
                    {score}
                    <span className="text-sm text-neutral-500">/100</span>
                  </p>
                  <span className={`text-xs px-3 py-1 border ${risk.bg} ${risk.color}`}>
                    {risk.level} Risk
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Top Critical Gaps / Areas for Improvement */}
        <section className="bg-white/5 backdrop-blur border border-white/10 p-8 mb-8">
          <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-6 block">
            {isNewWeaknessFormat ? 'Top 5 Critical Gaps' : 'Top Areas for Improvement'}
          </span>
          <div className="space-y-4">
            {isNewWeaknessFormat ? (
              diagnostic?.top_weaknesses?.map((w, i) => {
                const risk =
                  w.score >= 70
                    ? {
                        level: 'Low',
                        color: 'text-green-400',
                        bg: 'bg-green-900/30 border-green-700/50',
                        border: 'border-green-500',
                      }
                    : w.score >= 50
                    ? {
                        level: 'Moderate',
                        color: 'text-yellow-400',
                        bg: 'bg-yellow-900/30 border-yellow-700/50',
                        border: 'border-yellow-500',
                      }
                    : {
                        level: 'High',
                        color: 'text-red-400',
                        bg: 'bg-red-900/30 border-red-700/50',
                        border: 'border-red-500',
                      };

                return (
                  <div
                    key={i}
                    className={`flex items-start border-l-4 ${risk.border} bg-[#0b1220] pl-6 pr-6 py-4 border border-white/10`}
                  >
                    <span className={`text-3xl font-serif font-bold mr-6 ${risk.color}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-white mb-1">{w.question_text}</p>
                      <p className="text-sm text-neutral-400">
                        Category: {w.category} | Score: {w.score}/{w.max_score}
                      </p>
                      <p className="text-xs text-neutral-500 mt-2">
                        Your answer: {w.answer}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              diagnostic?.top_weaknesses?.map((category, i) => {
                const score = diagnostic?.category_scores?.[category] || 0;
                const risk = getRisk(score);
                const borderColor =
                  risk.level === 'Low'
                    ? 'border-green-500'
                    : risk.level === 'Moderate'
                    ? 'border-yellow-500'
                    : 'border-red-500';

                return (
                  <div
                    key={i}
                    className={`flex items-center border-l-4 ${borderColor} bg-[#0b1220] pl-6 pr-6 py-4 border border-white/10`}
                  >
                    <span className={`text-3xl font-serif font-bold mr-6 ${risk.color}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-white">{category}</p>
                      <p className="text-sm text-neutral-400">Score: {score}/100</p>
                    </div>
                    <span className={`text-sm px-4 py-1 border ${risk.bg} ${risk.color}`}>
                      {risk.level} Risk
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Detailed Analysis */}
        {diagnostic?.ai_report && (
          <section className="bg-white/5 backdrop-blur border border-white/10 p-8 mb-8">
            <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-6 block">
              Detailed Analysis
            </span>
            <div className="prose prose-invert max-w-none">
              {diagnostic?.ai_report?.split('\n').map(
                (p, i) =>
                  p.trim() && (
                    <p key={i} className="mb-4 text-neutral-300 leading-relaxed">
                      {p}
                    </p>
                  )
              )}
            </div>
          </section>
        )}

        {/* Contact CTA */}
        <div className="text-center py-12">
          <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-4 block">
            Ready to Improve?
          </span>
          <h2 className="text-3xl font-serif font-bold text-white mb-6">
            Let&apos;s Discuss Your Results
          </h2>
          <a
            href="mailto:george@ralrealadvisor.cy?subject=RAL Diagnostic Follow-up"
            className="inline-block px-10 py-4 bg-[#9fc6ff] text-[#05070d] font-medium tracking-wide uppercase hover:opacity-90 transition-all"
          >
            Contact RAL for Consultation
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-950 border-t border-neutral-800 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <Link to="/" className="flex items-center justify-center gap-3 mb-4">
            <img
              src={logo}
              alt="RAL Real Advisor"
              className="w-10 h-10 object-contain"
            />
            <div className="flex items-center">
              <span className="text-2xl font-serif font-bold text-white">RAL</span>
              <span className="text-2xl font-serif font-light text-[#9fc6ff] ml-1">
                Real Advisor
              </span>
            </div>
          </Link>
          <p className="text-neutral-400 mb-2">RAL Real Advisor Ltd</p>
          <p className="text-neutral-600 text-sm">
            +357 99277774 | george@ralrealadvisor.cy
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Report;